import type { SpeechToTextPort, SpeechToTextRequest, SpeechToTextResult } from '../../../ports/SpeechToTextPort';
import { SpeechToTextError } from '../../../ports/SpeechToTextPort';
import { gaiaHttpUrl } from '../url/GaiaUrls';

type GaiaSpeechToTextResponse = {
  text?: unknown;
  transcript_id?: unknown;
  transcriptId?: unknown;
  provider?: unknown;
  duration_ms?: unknown;
  durationMs?: unknown;
  language?: unknown;
};

export class GaiaSpeechToTextHttp implements SpeechToTextPort {
  constructor(private baseUrl: string) {}

  async transcribe(request: SpeechToTextRequest): Promise<SpeechToTextResult> {
    const form = new FormData();
    const filename = filenameForContentType(request.contentType ?? request.audio.type);
    form.append('audio', request.audio, filename);
    appendText(form, 'experience', request.experienceId);
    appendText(form, 'session_id', request.sessionId);
    appendText(form, 'turn_id', request.turnId);
    appendText(form, 'content_type', request.contentType ?? request.audio.type);
    appendNumber(form, 'max_duration_seconds', request.maxDurationSeconds);

    let res: Response;
    try {
      res = await fetch(gaiaHttpUrl(this.baseUrl, '/api/v0.3/speech/transcriptions'), {
        method: 'POST',
        headers: { Authorization: `Bearer ${request.token}` },
        body: form,
      });
    } catch {
      throw new SpeechToTextError('network', 'Gaia transcription request did not reach the server');
    }
    if (!res.ok) throw classifyHttpFailure(res.status);

    let data: GaiaSpeechToTextResponse;
    try {
      data = await res.json();
    } catch {
      throw new SpeechToTextError('malformed-response', 'Gaia transcription response was not valid JSON');
    }
    return normalizeSpeechToTextResponse(data);
  }
}

function classifyHttpFailure(status: number): SpeechToTextError {
  if (status === 401 || status === 403) {
    return new SpeechToTextError('auth', `Gaia transcription rejected authentication: ${status}`);
  }
  if (status >= 500) {
    // Includes provider-unconfigured/unavailable server states; narrows once
    // Gaia ships typed provider errors (contract row C3).
    return new SpeechToTextError('provider-unavailable', `Gaia transcription unavailable: ${status}`);
  }
  return new SpeechToTextError('unknown', `Gaia transcription failed: ${status}`);
}

function normalizeSpeechToTextResponse(data: GaiaSpeechToTextResponse): SpeechToTextResult {
  const text = stringField(data.text);
  if (!text) throw new SpeechToTextError('malformed-response', 'Gaia transcription returned an empty transcript');

  return {
    text,
    transcriptId: stringField(data.transcript_id) ?? stringField(data.transcriptId),
    provider: stringField(data.provider),
    durationMs: numberField(data.duration_ms) ?? numberField(data.durationMs),
    language: stringField(data.language),
  };
}

function appendText(form: FormData, key: string, value: string | undefined): void {
  if (value && value.trim()) form.append(key, value.trim());
}

function appendNumber(form: FormData, key: string, value: number | undefined): void {
  if (typeof value === 'number' && Number.isFinite(value)) form.append(key, String(value));
}

function stringField(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function numberField(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function filenameForContentType(contentType: string | undefined): string {
  if (contentType?.includes('ogg')) return 'voice-turn.ogg';
  if (contentType?.includes('mp4')) return 'voice-turn.mp4';
  if (contentType?.includes('wav')) return 'voice-turn.wav';
  return 'voice-turn.webm';
}
